/**
 * Utility functions for processing and enhancing links in content
 */

/**
 * Check if a string is a valid URL
 */
export function isValidUrl(string) {
  try {
    const url = new URL(string);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch (_) {
    return false;
  }
}

/**
 * Extract domain from URL
 */
export function getDomain(url) {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

/**
 * Enhanced URL regex that matches URLs in text (standalone and inline in sentences).
 * Matches full URLs (http://, https://). Excludes trailing punctuation and brackets.
 * Lookahead ensures URL is followed by optional punctuation + whitespace/end, or a closing bracket,
 * so that e.g. "...form: https://example.com. This..." matches the URL correctly.
 */
export const URL_REGEX = /(https?:\/\/[^\s<>"']+?)(?=[.,!?;:]?\s|[.,!?;:]?$|[)\]}>])/g;

/**
 * Domain name regex that matches domain names without protocol
 * Matches: example.com, www.example.com, subdomain.example.com
 * Excludes patterns that look like numbered lists (e.g., "1.", "2.")
 */
export const DOMAIN_REGEX = /\b(?![0-9]+\.)(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}(?:\.[a-zA-Z]{2,})?\b(?![.,!?;:]*[a-zA-Z0-9])/g;

/**
 * Email address regex. Matches local@domain.tld so the full address can be linked
 * as a mailto:, instead of letting DOMAIN_REGEX grab only the domain portion.
 */
export const EMAIL_REGEX = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;

/**
 * Final labels that look like domains but are common file extensions (not public TLDs).
 * Omit extensions that are real TLDs (e.g. app, io, co, ai, dev, me, mobi, mov, md) so
 * shorthand domains like mysite.app still auto-link.
 */
const NON_TLD_FILE_EXTENSIONS = new Set([
  // Office docs
  'pdf',
  'doc',
  'docx',
  'rtf',
  'odt',
  'pages',
  'txt',
  'tex',
  'xls',
  'xlsx',
  'csv',
  'ods',
  'numbers',
  'ppt',
  'pptx',
  'key',
  'odp',
  'epub',
  // Images (omit .ai — TLD; Illustrator files keep prior behavior)
  'jpg',
  'jpeg',
  'png',
  'gif',
  'bmp',
  'svg',
  'webp',
  'tif',
  'tiff',
  'heic',
  'ico',
  'psd',
  // Audio / video (omit mov — TLD)
  'mp3',
  'wav',
  'flac',
  'aac',
  'ogg',
  'm4a',
  'opus',
  'mp4',
  'avi',
  'mkv',
  'webm',
  'wmv',
  // Archives / installers
  'zip',
  'rar',
  '7z',
  'tar',
  'gz',
  'bz2',
  'dmg',
  'iso',
  'exe',
  'msi',
  'apk',
  'ipa',
  'deb',
  'rpm',
  // Code / data (omit io, co, dev, me, app, tech — TLDs)
  'json',
  'xml',
  'yaml',
  'yml',
  'sql',
  'log',
  'env',
  'lock',
]);

/**
 * Convert plain URLs and domain names in text to clickable links
 */
export function convertUrlsToLinks(text) {
  if (!text || typeof text !== 'string') return text;

  // First, convert full URLs
  let html = text.replace(URL_REGEX, (url) => {
    return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:underline">${url}</a>`;
  });

  // Then, convert domain names (but not if they're already inside a link tag)
  // We need to be careful not to match domains that are already in <a> tags
  const domainRegex = new RegExp(DOMAIN_REGEX);
  const urls = extractUrls(text);

  // Process from end to start to avoid index shifting issues
  const replacements = [];
  urls.forEach(({ url, displayUrl, index, length, isFullUrl }) => {
    if (!isFullUrl) {
      replacements.push({
        index,
        length,
        url,
        displayUrl: displayUrl || url.replace(/^https?:\/\//, ''),
      });
    }
  });

  // Sort by index descending to replace from end to start
  replacements.sort((a, b) => b.index - a.index);

  replacements.forEach(({ index, length, url, displayUrl }) => {
    // Check if this position is already inside a link tag
    const beforeMatch = html.substring(0, index);
    const afterMatch = html.substring(index + length);
    const lastOpenTag = beforeMatch.lastIndexOf('<a');
    const lastCloseTag = beforeMatch.lastIndexOf('</a>');

    // Only replace if not inside a link tag
    if (lastOpenTag === -1 || (lastCloseTag !== -1 && lastCloseTag > lastOpenTag)) {
      const before = html.substring(0, index);
      const domain = html.substring(index, index + length);
      const after = html.substring(index + length);
      html = before + `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:underline">${displayUrl}</a>` + after;
    }
  });

  return html;
}

/**
 * Check if a string looks like a domain name
 */
export function isDomainName(str) {
  // Skip if it's just a number followed by a dot (like "1." or "2.")
  if (/^\d+\.$/.test(str.trim())) {
    return false;
  }

  const lastDot = str.lastIndexOf('.');
  if (lastDot >= 0) {
    const tld = str.slice(lastDot + 1).toLowerCase();
    if (NON_TLD_FILE_EXTENSIONS.has(tld)) {
      return false;
    }
  }

  // Domain name pattern: alphanumeric, dots, hyphens, ends with valid TLD
  // Must have at least one letter (not just numbers)
  const domainPattern = /^([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
  const hasLetter = /[a-zA-Z]/.test(str);

  return hasLetter && domainPattern.test(str) && !str.startsWith('http://') && !str.startsWith('https://');
}

/**
 * Extract all URLs and domain names from text
 */
export function extractUrls(text) {
  if (!text || typeof text !== 'string') return [];

  const urls = [];

  // First, find full URLs (http://, https://)
  let match;
  const urlRegex = new RegExp(URL_REGEX);
  while ((match = urlRegex.exec(text)) !== null) {
    urls.push({
      url: match[0],
      index: match.index,
      length: match[0].length,
      isFullUrl: true,
    });
  }

  // Next, find email addresses so the full address links as mailto: and so the
  // domain portion of an email is not separately linked below.
  const emailRanges = [];
  const emailRegex = new RegExp(EMAIL_REGEX);
  while ((match = emailRegex.exec(text)) !== null) {
    const email = match[0];
    const isPartOfUrl = urls.some(u =>
      match.index >= u.index && match.index < u.index + u.length
    );
    if (isPartOfUrl) continue;
    emailRanges.push({ start: match.index, end: match.index + email.length });
    urls.push({
      url: `mailto:${email}`,
      displayUrl: email,
      index: match.index,
      length: email.length,
      isFullUrl: false,
      isEmail: true,
    });
  }

  // Then, find domain names (but not if they're already part of a URL or email)
  const domainRegex = new RegExp(DOMAIN_REGEX);
  while ((match = domainRegex.exec(text)) !== null) {
    const domain = match[0];
    // Skip if this domain is already part of a URL we found
    const isPartOfUrl = urls.some(u =>
      match.index >= u.index && match.index < u.index + u.length
    );
    // Skip if this domain is the domain portion of an email address
    const isPartOfEmail = emailRanges.some(r =>
      match.index >= r.start && match.index < r.end
    );

    // Skip common false positives (like "2." at start of numbered lists)
    if (!isPartOfUrl && !isPartOfEmail && isDomainName(domain)) {
      urls.push({
        url: `https://${domain}`, // Add protocol for domain names
        displayUrl: domain, // Keep original for display
        index: match.index,
        length: domain.length,
        isFullUrl: false,
      });
    }
  }

  // Sort by index to process in order
  urls.sort((a, b) => a.index - b.index);

  return urls;
}

/**
 * Process HTML content to ensure all links open in new tabs
 */
export function ensureLinksOpenInNewTab(html) {
  if (!html || typeof html !== 'string') return html;
  if (typeof document === 'undefined') return html; // SSR safety check

  // Create a temporary DOM element
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  // Find all links and ensure they have target="_blank"
  const links = tempDiv.querySelectorAll('a');
  links.forEach((link) => {
    const href = link.getAttribute('href');
    if (href && (href.startsWith('http://') || href.startsWith('https://'))) {
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener noreferrer');
      // Add hover styles if not already present
      if (!link.className.includes('hover:underline')) {
        link.className = `${link.className} text-blue-600 hover:underline`.trim();
      }
    }
  });

  return tempDiv.innerHTML;
}

/**
 * Process HTML content to convert plain URLs to links
 */
export function processHtmlForUrls(html) {
  if (!html || typeof html !== 'string') return html;
  if (typeof document === 'undefined') return html; // SSR safety check

  // First, ensure existing links open in new tabs
  let processed = ensureLinksOpenInNewTab(html);

  // Then, find text nodes and convert plain URLs to links
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = processed;

  // Walk through text nodes and convert URLs
  const walker = document.createTreeWalker(
    tempDiv,
    NodeFilter.SHOW_TEXT,
    null,
    false
  );

  const textNodes = [];
  let node;
  while ((node = walker.nextNode())) {
    // Skip if parent is already a link
    if (node.parentElement && node.parentElement.tagName === 'A') {
      continue;
    }

    const text = node.textContent;
    let urls = extractUrls(text);
    // Fallback: when extractUrls misses (e.g. edge cases), try regex-based conversion for inline URLs
    if (urls.length === 0 && (text.includes('https://') || text.includes('http://'))) {
      const converted = convertUrlsToLinks(text);
      if (converted !== text) {
        const wrapper = document.createElement('div');
        wrapper.innerHTML = converted;
        const fragment = document.createDocumentFragment();
        while (wrapper.firstChild) fragment.appendChild(wrapper.firstChild);
        node.parentNode.replaceChild(fragment, node);
      }
      continue;
    }
    if (urls.length > 0) {
      textNodes.push({ node, urls, text });
    }
  }

  // Replace text nodes with links
  textNodes.forEach(({ node, urls, text }) => {
    const fragment = document.createDocumentFragment();
    let lastIndex = 0;

    urls.forEach(({ url, displayUrl, index, length }) => {
      // Add text before URL
      if (index > lastIndex) {
        fragment.appendChild(document.createTextNode(text.substring(lastIndex, index)));
      }

      // Create link element
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.className = 'text-blue-600 hover:underline';
      // Use displayUrl if available (for domain names), otherwise use full url
      link.textContent = displayUrl || url;
      fragment.appendChild(link);

      lastIndex = index + length;
    });

    // Add remaining text
    if (lastIndex < text.length) {
      fragment.appendChild(document.createTextNode(text.substring(lastIndex)));
    }

    // Replace the original text node
    node.parentNode.replaceChild(fragment, node);
  });

  return tempDiv.innerHTML;
}

/**
 * Expand an href or bare URL into comparable keys (raw, resolved absolute, path+query)
 * so markdown links and pasted URLs dedupe across origins and trailing slashes.
 * @param {string} href
 * @returns {Set<string>}
 */
export function expandUrlDedupeKeys(href) {
  const keys = new Set();
  if (!href || typeof href !== 'string') return keys;
  keys.add(href);
  try {
    const base =
      typeof window !== 'undefined' && window.location?.href
        ? window.location.href
        : 'http://localhost/';
    const u = new URL(href, base);
    u.hash = '';
    keys.add(u.href.replace(/\/$/, ''));
    keys.add(`${u.pathname}${u.search}`);
    const pathTrim = u.pathname.replace(/\/$/, '') || '/';
    keys.add(`${pathTrim}${u.search}`);
  } catch (_) {
    /* ignore */
  }
  return keys;
}

export function urlsOverlapForDedupe(hrefA, hrefB) {
  const a = expandUrlDedupeKeys(hrefA);
  for (const k of expandUrlDedupeKeys(hrefB)) {
    if (a.has(k)) return true;
  }
  return false;
}

/**
 * Remove redundant http(s) tokens outside `<a>` tags when the same destination
 * already appears in a markdown-style link on this fragment (share/chat inline HTML).
 *
 * @param {string} fragment
 * @returns {string}
 */
export function dedupeRedundantHttpUrlsAfterMarkdownAnchors(fragment) {
  if (!fragment || typeof fragment !== 'string') return fragment;

  let result = fragment.replace(
    /<\/a>(\s+)(https?:\/\/[^\s<>"]+?)(?=[.,!?;:]?\s|[.,!?;:]?$|[)}\]])/gi,
    (full, _ws, url, offset, str) => {
      const before = str.slice(0, offset);
      const lastOpen = before.lastIndexOf('<a ');
      if (lastOpen === -1) return full;
      const hrefMatch = before.slice(lastOpen).match(/href="([^"]+)"/i);
      if (!hrefMatch) return full;
      return urlsOverlapForDedupe(hrefMatch[1], url) ? '</a>' : full;
    }
  );

  const linkedKeys = new Set();
  let hm;
  const hrefScan = /<a\b[^>]*href="([^"]+)"/gi;
  while ((hm = hrefScan.exec(result)) !== null) {
    expandUrlDedupeKeys(hm[1]).forEach((k) => linkedKeys.add(k));
  }

  const outsideAnchors = (html, fn) => {
    const parts = html.split(/(<a\b[^>]*>[\s\S]*?<\/a>)/gi);
    return parts.map((p, i) => (i % 2 === 1 ? p : fn(p))).join('');
  };

  result = outsideAnchors(result, (text) =>
    text.replace(
      /(https?:\/\/[^\s<>"]+?)(?=[.,!?;:]?\s|[.,!?;:]?$|[)}\]])/g,
      (full, url) => {
        for (const k of expandUrlDedupeKeys(url)) {
          if (linkedKeys.has(k)) return '';
        }
        return full;
      }
    )
  );

  return outsideAnchors(result, (text) => text.replace(/[ \t]{2,}/g, ' '));
}

/**
 * After HTML is assembled, drop a leading bare URL in text nodes that duplicates
 * the previous sibling anchor (covers injectRAG + paragraph wrapping).
 *
 * @param {string} html
 * @returns {string}
 */
export function stripRedundantBareUrlsAfterAnchors(html) {
  if (!html || typeof document === 'undefined') return html;

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  const walker = document.createTreeWalker(tempDiv, NodeFilter.SHOW_TEXT, null, false);
  const textNodes = [];
  let node;
  while ((node = walker.nextNode())) {
    if (node.parentElement && node.parentElement.tagName === 'A') continue;
    textNodes.push(node);
  }

  for (const textNode of textNodes) {
    let prev = textNode.previousSibling;
    while (
      prev &&
      prev.nodeType === Node.TEXT_NODE &&
      prev.textContent.replace(/\s/g, '') === ''
    ) {
      prev = prev.previousSibling;
    }
    if (!prev || prev.nodeType !== Node.ELEMENT_NODE || prev.tagName !== 'A') continue;

    const href = prev.getAttribute('href');
    if (!href) continue;

    const text = textNode.textContent;
    const m = text.match(/^(\s*)(https?:\/\/[^\s<>"']+)/i);
    if (!m) continue;

    const leadingWs = m[1];
    const bare = m[2];
    if (!urlsOverlapForDedupe(href, bare)) continue;

    const tail = text.slice(m[0].length).replace(/^[.,!?;:\s]+/, '');
    textNode.textContent = leadingWs.replace(/\s+$/, '') + tail;
  }

  return tempDiv.innerHTML;
}
